import cn from "clsx";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

export type CalculatedData = {
  retailPrice: string | number;
  margin: string | number;
  profit: string | number;
};

type CalculatedFieldProps = {
  name: string;
  value: string | number;
  valuePrefix?: string;
  className?: string;
  showDivider?: boolean;
};

function CalculatedField({
  name,
  value,
  valuePrefix,
  showDivider,
  className,
}: CalculatedFieldProps) {
  return (
    <div
      className={cn(`flex w-full flex-wrap items-center justify-between py-3 ${className}`, {
        "border-b border-grey-40 border-dashed": showDivider,
      })}
    >
      <p className="w-1/2 text-lg">{name}</p>
      <div className="flex w-1/2 flex-col items-end">
        <p className="mb-0 text-lg font-semibold">{value}</p>
        {valuePrefix && <p className="text-sm">{valuePrefix}</p>}
      </div>
    </div>
  );
}

type CalculatedFieldsProps = {
  calculated: CalculatedData;
  unitPrice: string;
  className?: string;
};

export function CalculatedFields({ calculated, unitPrice, className }: CalculatedFieldsProps) {
  const t = useTranslations("common");

  const fields = useMemo(() => {
    const items = [
      {
        name: t("text-retail-price"),
        value: calculated.retailPrice,
        valuePrefix: `${t("text-unit-price")}: ${unitPrice}`,
        showDivider: true,
      },
      {
        name: `${t("text-profit")} (${t("text-gross")})`,
        value: calculated.profit,
        showDivider: true,
      },
      {
        name: `${t("text-margin")} (${t("text-gross")})`,
        value: calculated.margin,
      },
    ];
    return items.map((item) => <CalculatedField key={item.name} {...item} />);
  }, [calculated, unitPrice]);

  return <div className={className}>{fields}</div>;
}
